import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ImageUp, RotateCcw, Save, Type, X } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { AssinaturaMalgo } from '../../componentes/assinatura';
import { Entrada, Rotulo } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { RecorteImagem, carregarImagem, exportarRecorte, type ImagemCarregada, type ModoRecorte, type ResultadoRecorte } from '../../componentes/recorte-imagem';
import { api, del, get, mensagemDeErro, patch } from '../../nucleo/api';
import { dataHora } from '../../nucleo/formato';
import { CHAVE_TELA_ENTRADA, LIMITES_TEXTOS, TEXTOS_PADRAO, urlFundo, urlLogo, type TelaEntrada as DadosTela, type TextosTelaEntrada } from '../../nucleo/tela-entrada';
import farolWebp1280 from '../../estilos/login/farol-1280.webp';

/**
 * Tela de entrada (30/10, ficha `2026-10-30_malgo_FUN_tela-de-entrada-logo-e-fundo`): Admin > Painel admin > Tela de entrada.
 * Só o administrador da plataforma (aba `plataforma`; o servidor confere de novo). Para o fundo e para a logo: as dicas de
 * tamanho, a imagem em uso, "Escolher imagem" abre a ferramenta de ajuste (recorte, zoom, tamanho final e compressão no
 * navegador), o resultado entra na prévia antes de salvar, e "Voltar ao padrão" restaura o farol ou a logo da Malgo. A prévia
 * mostra o computador e o celular nos temas claro e escuro.
 */

const CHAVE = ['/nucleo/tela-entrada'];
const LIMITE_ABRIR = 20 * 1024 * 1024;
const TIPOS_ACEITOS = ['image/png', 'image/jpeg', 'image/webp'];

/** Ferramentas trocáveis nos testes (o DOM simulado não decodifica imagem nem tem canvas). */
export const ferramentasTelaEntrada = { carregar: carregarImagem, exportar: exportarRecorte };

/** As imagens da tela de entrada (o recorte também tem o modo `avatar`, da foto do perfil). */
type TipoTela = Exclude<ModoRecorte, 'avatar'>;

interface Pendente extends ResultadoRecorte {
  url: string;
}

const kb = (bytes: number, idioma: string) => `${Math.max(1, Math.round(bytes / 1024)).toLocaleString(idioma)} KB`;

function CartaoImagem({ tipo, atual, pendente, aoPendente }: { tipo: TipoTela; atual: DadosTela[TipoTela]; pendente: Pendente | null; aoPendente: (p: Pendente | null) => void }) {
  const { t, i18n } = useTranslation('admin');
  const qc = useQueryClient();
  const toast = useToast();
  const entrada = useRef<HTMLInputElement>(null);
  const [aberta, setAberta] = useState<ImagemCarregada | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  useEffect(() => () => aberta?.liberar(), [aberta]);

  const depois = async () => {
    await qc.invalidateQueries({ queryKey: CHAVE });
    await qc.invalidateQueries({ queryKey: CHAVE_TELA_ENTRADA });
  };
  const salvar = useMutation({
    mutationFn: (p: Pendente) => {
      const f = new FormData();
      const ext = p.blob.type === 'image/webp' ? 'webp' : p.blob.type === 'image/png' ? 'png' : 'jpg';
      f.append('arquivo', new File([p.blob], `${tipo}.${ext}`, { type: p.blob.type }));
      return api<DadosTela>('POST', `/nucleo/tela-entrada/${tipo}`, f);
    },
    onSuccess: async () => {
      aoPendente(null);
      await depois();
      toast.sucesso(t('telaEntrada.salva'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const restaurar = useMutation({
    mutationFn: () => del<DadosTela>(`/nucleo/tela-entrada/${tipo}`),
    onSuccess: async () => {
      setConfirmar(false);
      aoPendente(null);
      await depois();
      toast.sucesso(t('telaEntrada.restaurada'));
    },
    onError: (e) => {
      setConfirmar(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const escolher = async (arquivo: File | undefined) => {
    if (entrada.current) entrada.current.value = '';
    if (!arquivo) return;
    setErro(null);
    // O servidor confere pelos bytes; aqui só evita abrir o que não é PNG, JPG ou WebP (SVG fica de fora).
    if (!TIPOS_ACEITOS.includes(arquivo.type)) return setErro(t('telaEntrada.tipoInvalido'));
    if (arquivo.size > LIMITE_ABRIR) return setErro(t('telaEntrada.grandeDemais'));
    try {
      const img = await ferramentasTelaEntrada.carregar(arquivo);
      setAberta(img);
    } catch {
      setErro(t('telaEntrada.naoAbriu'));
    }
  };
  const concluir = (r: ResultadoRecorte) => {
    aoPendente({ ...r, url: URL.createObjectURL(r.blob) });
    setAberta(null);
  };

  const urlAtual = atual ? (tipo === 'fundo' ? urlFundo(atual.versao, 768, 'webp') : urlLogo(atual.versao, 'png')) : null;
  const id = `tela-entrada-${tipo}`;
  return (
    <Cartao titulo={t(`telaEntrada.${tipo}.titulo`)}>
      <div className="flex min-w-0 flex-col gap-4" data-tela-entrada-cartao={tipo}>
        <div className="rounded-lg border border-borda bg-sup-2 p-3" data-tela-entrada-dicas={tipo}>
          <p className="text-xs font-semibold tracking-wide text-tinta-3 uppercase">{t('telaEntrada.dicasTitulo')}</p>
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-5 text-sm text-tinta-2">
            {(['dica1', 'dica2', 'dica3', 'dica4'] as const).map((d) => (
              <li key={d}>{t(`telaEntrada.${tipo}.${d}`)}</li>
            ))}
          </ul>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-3" data-tela-entrada-atual={atual ? 'personalizada' : 'padrao'}>
          <div className={`flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-borda bg-sup-2 ${tipo === 'fundo' ? 'aspect-video w-32' : 'h-12 w-32 p-1'}`}>
            {tipo === 'fundo' ? (
              <img src={urlAtual ?? farolWebp1280} alt={t('telaEntrada.atualAlt')} className="size-full object-cover" />
            ) : urlAtual ? (
              <img src={urlAtual} alt={t('telaEntrada.atualAlt')} className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-tinta-1"><AssinaturaMalgo className="h-6" /></span>
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <Selo cor={atual ? 'verde' : 'neutro'}>{atual ? t('telaEntrada.personalizada') : t('telaEntrada.padrao')}</Selo>
            <p className="text-xs text-tinta-3">{atual?.atualizado_em ? t('telaEntrada.atualizadaEm', { quando: dataHora(atual.atualizado_em) }) : t(`telaEntrada.${tipo}.padraoTexto`)}</p>
          </div>
        </div>

        {aberta ? (
          <RecorteImagem imagem={aberta} modo={tipo} aoConcluir={concluir} aoCancelar={() => setAberta(null)} exportar={ferramentasTelaEntrada.exportar} />
        ) : (
          pendente && (
            <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-borda p-3" data-tela-entrada-pendente={tipo}>
              <img src={pendente.url} alt={t('telaEntrada.resultadoAlt')} className={`rounded-md border border-borda ${tipo === 'fundo' ? 'aspect-video w-full object-cover' : 'max-h-20 max-w-full self-start object-contain'}`} />
              <p className="text-sm text-tinta-2">{t('telaEntrada.resultado', { largura: pendente.largura, altura: pendente.altura, tamanho: kb(pendente.blob.size, i18n.language) })}</p>
            </div>
          )
        )}

        {salvar.isPending && (
          <p role="status" className="text-sm text-tinta-2" data-tela-entrada-processando={tipo}>
            {t('telaEntrada.processando')}
          </p>
        )}
        {erro && (
          <p id={`${id}-erro`} role="alert" className="text-sm text-perigo" data-tela-entrada-erro>
            {erro}
          </p>
        )}

        {!aberta && (
          <div className="flex flex-wrap justify-end gap-2">
            <input
              ref={entrada}
              id={id}
              type="file"
              accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              className="sr-only"
              tabIndex={-1}
              title={t(`telaEntrada.${tipo}.titulo`)}
              onChange={(e) => void escolher(e.target.files?.[0])}
              data-tela-entrada-arquivo={tipo}
            />
            {atual && !pendente && (
              <Botao variante="fantasma" icone={<RotateCcw className="size-4" aria-hidden />} onClick={() => setConfirmar(true)} data-tela-entrada-padrao={tipo}>
                {t('telaEntrada.voltarPadrao')}
              </Botao>
            )}
            {pendente ? (
              <>
                <Botao variante="fantasma" icone={<X className="size-4" aria-hidden />} disabled={salvar.isPending} onClick={() => aoPendente(null)}>
                  {t('telaEntrada.descartar')}
                </Botao>
                <Botao icone={<Save className="size-4" aria-hidden />} carregando={salvar.isPending} onClick={() => salvar.mutate(pendente)} data-tela-entrada-salvar={tipo}>
                  {t('telaEntrada.salvar')}
                </Botao>
              </>
            ) : (
              <Botao variante="secundario" icone={<ImageUp className="size-4" aria-hidden />} aria-describedby={erro ? `${id}-erro` : undefined} onClick={() => entrada.current?.click()} data-tela-entrada-escolher={tipo}>
                {atual ? t('telaEntrada.trocar') : t('telaEntrada.escolher')}
              </Botao>
            )}
          </div>
        )}
      </div>
      <Confirmar
        aberto={confirmar}
        aoFechar={() => setConfirmar(false)}
        aoConfirmar={() => restaurar.mutate()}
        titulo={t('telaEntrada.confirmarPadraoTitulo')}
        texto={tipo === 'fundo' ? t('telaEntrada.confirmarPadraoFundo') : t('telaEntrada.confirmarPadraoLogo')}
        carregando={restaurar.isPending}
      />
    </Cartao>
  );
}

type CampoTexto = keyof TextosTelaEntrada;
const CAMPOS_TEXTO: { campo: CampoTexto; padrao: string }[] = [
  { campo: 'kicker', padrao: 'login.kicker' },
  { campo: 'titulo', padrao: 'login.marca' },
  { campo: 'subtitulo', padrao: 'login.subtitulo' },
];

/**
 * Lista de 30/10 (item 1): os textos da faixa da marca (chamada, título e subtítulo). Campo vazio = o texto padrão do idioma
 * (mostrado no placeholder); o texto gravado vale para todos os idiomas. O servidor junta quebras e espaços e confere o tamanho.
 */
function CartaoTextos({ atual }: { atual: TextosTelaEntrada }) {
  const { t } = useTranslation('admin');
  const { t: tGeral } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const inicial = (): Record<CampoTexto, string> => ({ kicker: atual.kicker ?? '', titulo: atual.titulo ?? '', subtitulo: atual.subtitulo ?? '' });
  const [valores, setValores] = useState(inicial);
  const [erro, setErro] = useState<string | null>(null);
  const chaveAtual = JSON.stringify(atual);
  // Depois de salvar (ou de outra aba gravar), o formulário volta ao que está gravado.
  useEffect(() => setValores(inicial()), [chaveAtual]); // eslint-disable-line react-hooks/exhaustive-deps
  const limpo = (v: string) => v.replace(/\s+/g, ' ').trim();
  const mudou = CAMPOS_TEXTO.some(({ campo }) => limpo(valores[campo]) !== (atual[campo] ?? ''));
  const personalizado = CAMPOS_TEXTO.some(({ campo }) => atual[campo] !== null);
  const salvar = useMutation({
    mutationFn: (v: Record<CampoTexto, string | null>) => patch<DadosTela>('/nucleo/tela-entrada/textos', v),
    onSuccess: async (_d, v) => {
      setErro(null);
      await qc.invalidateQueries({ queryKey: CHAVE });
      await qc.invalidateQueries({ queryKey: CHAVE_TELA_ENTRADA });
      toast.sucesso(CAMPOS_TEXTO.every(({ campo }) => v[campo] === null) ? t('telaEntrada.textos.restaurados') : t('telaEntrada.textos.salvos'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const enviar = (padrao: boolean) =>
    salvar.mutate({
      kicker: padrao ? null : limpo(valores.kicker) || null,
      titulo: padrao ? null : limpo(valores.titulo) || null,
      subtitulo: padrao ? null : limpo(valores.subtitulo) || null,
    });
  return (
    <Cartao titulo={t('telaEntrada.textos.cartao')}>
      <form
        className="flex min-w-0 flex-col gap-4"
        data-tela-entrada-textos
        onSubmit={(e) => {
          e.preventDefault();
          enviar(false);
        }}
      >
        <p className="text-sm text-tinta-2">{t('telaEntrada.textos.ajuda')}</p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {CAMPOS_TEXTO.map(({ campo, padrao }) => {
            const id = `tela-entrada-texto-${campo}`;
            return (
              <Rotulo key={campo} id={id} rotulo={t(`telaEntrada.textos.${campo}`)} ajuda={t('telaEntrada.textos.limite', { usados: valores[campo].length, max: LIMITES_TEXTOS[campo] })}>
                <Entrada
                  id={id}
                  value={valores[campo]}
                  maxLength={LIMITES_TEXTOS[campo]}
                  placeholder={tGeral(padrao)}
                  aria-describedby={`${id}-ajuda`}
                  onChange={(e) => setValores((x) => ({ ...x, [campo]: e.target.value }))}
                  data-tela-entrada-texto={campo}
                />
              </Rotulo>
            );
          })}
        </div>
        {erro && (
          <p role="alert" className="text-sm text-perigo" data-tela-entrada-textos-erro>
            {erro}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          {personalizado && (
            <Botao type="button" variante="fantasma" icone={<RotateCcw className="size-4" aria-hidden />} disabled={salvar.isPending} onClick={() => enviar(true)} data-tela-entrada-textos-padrao>
              {t('telaEntrada.textos.voltarPadrao')}
            </Botao>
          )}
          <Botao type="submit" icone={<Type className="size-4" aria-hidden />} carregando={salvar.isPending} disabled={!mudou} data-tela-entrada-textos-salvar>
            {t('telaEntrada.textos.salvar')}
          </Botao>
        </div>
      </form>
    </Cartao>
  );
}

type Tema = 'claro' | 'escuro';
/** Rodada rlogin-1 (VIS-M1): a paleta da Malgo (faixa da marca escura) e a dos estilos de faixa clara (Cupertino e outros). */
type Paleta = 'malgo' | 'clara';
const cor = (v: string): CSSProperties => ({ backgroundColor: `var(${v})` });

/** Logo da prévia: a configurada (ou a pendente) sobre a placa da paleta e, sem ela, o símbolo e o nome da Malgo. */
function LogoPrevia({ url, celular, prefixo }: { url: string | null; celular: boolean; prefixo: string }) {
  if (url)
    return (
      <span className="block w-fit rounded-sm p-0.5" style={cor(`${prefixo}-placa`)} data-previa-placa>
        <img src={url} alt="" className={`block w-auto object-contain ${celular ? 'max-h-3 max-w-16' : 'max-h-4 max-w-24'}`} data-previa-logo="personalizada" />
      </span>
    );
  return (
    <span className="flex items-center gap-1" data-previa-logo="padrao" style={{ color: `var(${prefixo}-marca-tinta)` }}>
      <AssinaturaMalgo className={celular ? 'h-3' : 'h-4'} />
      <span className="sr-only">MalgoOS</span>
    </span>
  );
}

/** Formulário de mentira (barras), na tinta e no acento do tema. */
function FormularioPrevia({ prefixo }: { prefixo: string }) {
  const tinta = cor(`${prefixo}-tinta`);
  return (
    <div className="flex w-3/5 flex-col gap-1.5" aria-hidden>
      <span className="h-1.5 w-1/2 rounded-full opacity-70" style={tinta} />
      <span className="h-2.5 w-full rounded-sm opacity-15" style={tinta} />
      <span className="h-2.5 w-full rounded-sm opacity-15" style={tinta} />
      <span className="h-2.5 w-full rounded-sm" style={cor(`${prefixo}-acento`)} />
    </div>
  );
}

function Quadro({ paleta, tema, celular, fundo, logo }: { paleta: Paleta; tema: Tema; celular: boolean; fundo: string | null; logo: string | null }) {
  const { t } = useTranslation('admin');
  const prefixo = `--previa-${paleta}-${tema}`;
  const legenda = t('telaEntrada.previa.legenda', { aparelho: t(celular ? 'telaEntrada.previa.celular' : 'telaEntrada.previa.computador'), tema: t(`telaEntrada.previa.${tema}`) });
  // O farol ancora à esquerda no celular (onde fica o farol), como na tela de verdade; a imagem configurada fica no centro.
  const posicao = !fundo && celular ? '30% center' : 'center';
  return (
    <figure className={`flex min-w-0 flex-col gap-1.5 ${celular ? 'w-28 shrink-0' : 'w-full'}`} data-previa-quadro={`${paleta}-${celular ? 'celular' : 'computador'}-${tema}`}>
      <div className={`relative overflow-hidden rounded-lg border border-borda ${celular ? 'w-full' : 'aspect-video w-full'}`} style={{ ...cor('--previa-login-fundo'), ...(celular ? { aspectRatio: '9 / 19.5' } : {}) }}>
        <img src={fundo ?? farolWebp1280} alt="" className="absolute inset-0 size-full object-cover" style={{ objectPosition: posicao }} data-previa-fundo={fundo ? 'personalizado' : 'padrao'} />
        {celular ? (
          <div className="absolute inset-0 flex flex-col">
            <div className="flex items-center px-2 py-2" style={cor(`${prefixo}-veu-marca`)}>
              <LogoPrevia url={logo} celular prefixo={prefixo} />
            </div>
            <div className="flex flex-1 items-center justify-center" style={cor(`${prefixo}-veu-form`)}>
              <FormularioPrevia prefixo={prefixo} />
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 grid grid-cols-2">
            <div className="flex flex-col p-3" style={cor(`${prefixo}-veu-marca`)}>
              <LogoPrevia url={logo} celular={false} prefixo={prefixo} />
            </div>
            <div className="flex items-center justify-center" style={cor(`${prefixo}-veu-form`)}>
              <FormularioPrevia prefixo={prefixo} />
            </div>
          </div>
        )}
      </div>
      <figcaption className="text-xs text-tinta-3">{legenda}</figcaption>
    </figure>
  );
}

export function PreviaTelaEntrada({ fundo, logo }: { fundo: string | null; logo: string | null }) {
  const { t } = useTranslation('admin');
  return (
    <Cartao titulo={t('telaEntrada.previa.titulo')}>
      <div className="flex min-w-0 flex-col gap-4" data-tela-entrada-previa>
        <p className="text-sm text-tinta-3">{t('telaEntrada.previa.texto')}</p>
        {(['malgo', 'clara'] as const).map((paleta) => (
          <section key={paleta} className="flex min-w-0 flex-col gap-2" data-previa-paleta={paleta}>
            <h3 className="text-xs font-semibold tracking-wide text-tinta-3 uppercase">{t(`telaEntrada.previa.${paleta}`)}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {(['claro', 'escuro'] as const).map((tema) => (
                <div key={tema} className="flex min-w-0 flex-wrap items-start gap-3 sm:flex-nowrap">
                  <Quadro paleta={paleta} tema={tema} celular={false} fundo={fundo} logo={logo} />
                  <Quadro paleta={paleta} tema={tema} celular fundo={fundo} logo={logo} />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </Cartao>
  );
}

export function TelaEntrada() {
  const { t } = useTranslation('admin');
  const q = useQuery({ queryKey: CHAVE, queryFn: () => get<DadosTela>('/nucleo/tela-entrada') });
  const [pendentes, setPendentes] = useState<Record<TipoTela, Pendente | null>>({ fundo: null, logo: null });
  const definir = (tipo: TipoTela) => (p: Pendente | null) =>
    setPendentes((atual) => {
      if (atual[tipo]) URL.revokeObjectURL(atual[tipo].url);
      return { ...atual, [tipo]: p };
    });
  return (
    <Estado consulta={q}>
      {(d) => {
        const fundo = pendentes.fundo?.url ?? (d.fundo ? urlFundo(d.fundo.versao, 1280, 'webp') : null);
        const logo = pendentes.logo?.url ?? (d.logo ? urlLogo(d.logo.versao, 'png') : null);
        return (
          <div className="flex min-w-0 flex-col gap-5" data-tela-entrada>
            <p className="text-sm text-tinta-2">{t('telaEntrada.intro')}</p>
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <CartaoImagem tipo="fundo" atual={d.fundo} pendente={pendentes.fundo} aoPendente={definir('fundo')} />
              <CartaoImagem tipo="logo" atual={d.logo} pendente={pendentes.logo} aoPendente={definir('logo')} />
            </div>
            <CartaoTextos atual={d.textos ?? TEXTOS_PADRAO} />
            <PreviaTelaEntrada fundo={fundo} logo={logo} />
          </div>
        );
      }}
    </Estado>
  );
}
